import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { siteUrl } from '@/lib/site';
import { getArticleBySlug } from '@/mocks/articles';
import { MAIN_SITE_URL } from '@/mocks/categories';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mt-10 mb-4">{children}</h2>;
}

function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-stone-700 text-sm md:text-base">
          <i className="ri-check-double-line text-emerald-600 w-5 h-5 flex items-center justify-center mt-0.5" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default function GuidePage() {
  const { t } = useTranslation();
  const params = useParams();
  const slug = params.slug || '';

  const articleMeta = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!articleMeta) {
    return (
      <div className="min-h-screen bg-cream text-stone-900">
        <Header />
        <main className="mx-auto max-w-3xl px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-stone-900">{t('art.notFoundTitle', 'Article Not Found')}</h1>
          <p className="mt-4 text-stone-600">{t('art.notFoundDesc', 'The article you are looking for does not exist.')}</p>
          <Link to="/" className="mt-6 inline-block rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
            {t('art.ctaHome')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const k = (key: string) => `articles.${slug}.${key}`;

  const hasChecklist = t(k('checkTitle'), '') !== '';
  const checklistItems = hasChecklist
    ? [t(k('check1'), ''), t(k('check2'), ''), t(k('check3'), ''), t(k('check4'), ''), t(k('check5'), '')].filter(Boolean)
    : [];

  const faqs = [
    { q: t(k('faq1Q')), a: t(k('faq1A')) },
    { q: t(k('faq2Q')), a: t(k('faq2A')) },
    { q: t(k('faq3Q')), a: t(k('faq3A')) },
  ];

  const articleData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: t(k('title')),
    description: t(k('subtitle')),
    image: articleMeta.image,
    author: { '@type': 'Organization', name: 'USFans Editorial Team' },
    publisher: { '@type': 'Organization', name: 'USFans Spreadsheet', url: siteUrl('/') },
    datePublished: articleMeta.updated,
    dateModified: articleMeta.updated,
    mainEntityOfPage: siteUrl(`/guide/${slug}`),
  };

  const faqData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const sections = [];
  for (let i = 1; i <= 5; i++) {
    const h = t(k(`h${i}`), '');
    const p = t(k(`p${i}`), '');
    if (h || p) {
      sections.push({ heading: h, body: p });
    }
  }

  return (
    <div className="min-h-screen bg-cream text-stone-900">
      <JsonLd data={articleData} />
      <JsonLd data={faqData} />
      <Header />
      <main className="mx-auto max-w-3xl px-4 md:px-6 py-10 md:py-14">
        <nav className="flex items-center gap-2 text-sm text-stone-500 flex-wrap">
          <Link to="/" className="hover:text-emerald-700 cursor-pointer whitespace-nowrap">
            {t('art.breadcrumbHome')}
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-stone-400 whitespace-nowrap">{t('art.breadcrumbGuides')}</span>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-emerald-700 font-medium whitespace-nowrap">{t(k('breadcrumbCurrent'), t(k('title')))}</span>
        </nav>

        <header className="mt-6">
          <span className="inline-block rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
            {t('home.navGuides')}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-stone-900 leading-tight">
            {t(k('title'))}
          </h1>
          <p className="mt-4 text-base md:text-lg text-stone-600">{t(k('subtitle'))}</p>
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-stone-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
              {t('art.writtenBy')}
            </span>
            <span className="hidden sm:inline text-stone-300">•</span>
            <span>{t('art.updated')} {articleMeta.updated}</span>
          </div>
        </header>

        <div className="mt-8 rounded-lg overflow-hidden border border-stone-200 bg-white">
          <img
            src={articleMeta.image}
            alt={t(k('title'))}
            title={t(k('title'))}
            className="w-full h-52 md:h-72 object-cover object-top"
          />
        </div>

        <article className="mt-8">
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t(k('intro'))}</p>

          {sections.map((s, idx) => (
            <div key={idx}>
              {s.heading && <SectionHeading>{s.heading}</SectionHeading>}
              {s.body && <p className="text-sm md:text-base text-stone-700 leading-relaxed">{s.body}</p>}
            </div>
          ))}

          {hasChecklist && (
            <div className="mt-10 rounded-lg border border-stone-200 bg-white p-5 md:p-6">
              <h3 className="font-heading text-lg md:text-xl font-bold text-stone-900 mb-4">{t(k('checkTitle'))}</h3>
              <CheckList items={checklistItems} />
            </div>
          )}

          <SectionHeading>{t('home.faqTitle')}</SectionHeading>
          <div className="space-y-3">
            {faqs.map((f, idx) => (
              <div key={idx} className="rounded-lg border border-stone-200 bg-white p-5">
                <h3 className="font-heading font-semibold text-stone-900 text-sm md:text-base">{f.q}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-lg bg-emerald-50 border border-emerald-100 p-6">
            <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t(k('conclusion'))}</p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 text-center whitespace-nowrap cursor-pointer"
              >
                {t('art.ctaHome')}
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="rounded-md border border-emerald-300 px-5 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 text-center whitespace-nowrap cursor-pointer"
              >
                {t('art.ctaSpreadsheet')}
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}